<script setup lang="ts">
import { computed } from 'vue'
import type { Notification } from '../../../../../../Types/notification'
import NotificationBadge from './NotificationBadge.vue'
import NotificationIcon from './NotificationIcon.vue'
import { useNotificationApi } from '../../../../../../Composables/Notification/useNotificationApi.ts'

interface Props {
  notifications: Notification[]
  isTeamView?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'notification-updated', notification: Notification): void
}>()

const { markAsRead } = useNotificationApi()

async function handleNotificationClick(notification: Notification): Promise<void> {
  if (!notification.isRead) {
    try {
      const updatedNotification = await markAsRead(notification)
      emit('notification-updated', updatedNotification)
    } catch {}
  }

  if (notification.link) {
    window.location.href = notification.link
  }
}

function formatDate(dateString?: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)

  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function getTranslationKey(translationKey: string): string {
  return translationKey.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

const getNotificationColorClass = (notification: Notification): string => {
  return notification.type === 'ALERT_ACTION_REQUIRED' ? 'text-danger' : 'text-primary'
}

const getAvatarBgClass = (notification: Notification): string => {
  return notification.type === 'ALERT_ACTION_REQUIRED' ? 'bg-pale-danger' : 'bg-pale-secondary'
}
</script>

<template>
  <div class="list-group list-group-flush notification-list">
    <div v-if="props.notifications.length === 0" class="p-4 text-center text-muted fs-13" @click.stop>
      {{ $t('notification.list.empty') }}
    </div>
    <a
      v-for="notification in props.notifications"
      :key="notification.id"
      class="dropdown-item py-2 px-3 border-bottom d-flex align-items-center text-wrap notification-item"
      @click.stop="handleNotificationClick(notification)"
    >
      <div class="position-relative mr-2 flex-shrink-0">
        <NotificationBadge v-if="!notification.isRead" variant="dot" />
        <div
          :class="[
            getAvatarBgClass(notification),
            getNotificationColorClass(notification),
            'avatar rounded-circle d-flex align-items-center justify-content-center',
          ]"
        >
          <NotificationIcon :context="notification.context" />
        </div>
      </div>
      <div class="flex-grow-1 mr-2">
        <div :class="[getNotificationColorClass(notification), 'font-weight-bold fs-10 mb-1']">
          {{ $t(getTranslationKey(notification.translationKey)) }}
        </div>

        <div v-if="props.isTeamView && notification.recipientName" class="mb-1">
          <span class="badge badge-light text-muted fs-9 px-1 py-0">
            {{ notification.recipientName }}
          </span>
        </div>

        <div class="font-weight-bold text-dark fs-13">
          {{ notification.title }}
        </div>
        <div class="text-muted fs-11">{{ notification.subtitle }}</div>
      </div>
      <div class="text-right flex-shrink-0">
        <small class="text-muted notification-date">
          {{ formatDate(notification.createdAt) }}
        </small>
      </div>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.notification-list {
  max-height: 35.55vh;
  overflow-y: auto;
}

.notification-item {
  white-space: normal;
}

.notification-date {
  font-size: 0.625rem;
}
</style>
